import { groupBy } from 'lodash';
import { FilterCategory } from 'models/enums';
import { Send } from 'domains/campaigns/types';
import {
    DateRange,
    isDateRangeType,
    isRelativeDateRangeType,
    NestedSegmentDefinitionInput,
    NullOperator,
    RecordTypeFilter,
    RelativeDateRange,
    SegmentDefinition,
    SegmentFilterCategorySpecificField,
    SegmentFilterDemographicFields,
    SegmentFilterEngagementFields,
    SegmentFilterEventCondition,
    SegmentFilterEventFields,
    SegmentFilterState,
} from 'domains/segments/types';
import {
    convertFilterToDemographic,
    convertFilterToEngagement,
    convertFilterToEvent,
    getDemographicFilter,
    getEngagementFilter,
    getEventFilter,
} from 'domains/segments/utils';
import { FilterGroupNode, FilterTreeNode } from './types';
import { createFilterLeafNode, flattenFilters, generateNodeId } from './treeUtils';

const isValidActivityFilter = (filter: SegmentFilterState<SegmentFilterEngagementFields>) => {
    const { categorySpecificFields, operator } = filter;
    const { value, sendInteractionField } = categorySpecificFields;

    let isSendInteractionValueInValid;
    if (sendInteractionField) {
        const autocompleteValueArray =
            Array.isArray(sendInteractionField.autocompleteValue) && sendInteractionField.autocompleteValue;

        isSendInteractionValueInValid =
            autocompleteValueArray?.length === 0 || sendInteractionField.autocompleteValue === undefined;
    }

    return !!operator && !!value && !isSendInteractionValueInValid;
};

const isValidDateRange = (value: DateRange) => !!value.start && !!value.end;
const isValidRelativeDateRange = (value: RelativeDateRange) => {
    const isValidDurationValue = !!value.duration.value || value.duration.value === 0;
    const isValidOffsetValue = !!value.offset.value || value.offset.value === 0;

    const isValidDurationUnit = !!value.duration.unit;
    const isValidOffsetUnit = !!value.offset.unit;

    return isValidDurationValue && isValidOffsetValue && isValidDurationUnit && isValidOffsetUnit;
};

const isValidValue = (value: any, operator: any) => {
    if (isDateRangeType(value)) return isValidDateRange(value);
    if (isRelativeDateRangeType(value)) return isValidRelativeDateRange(value);
    if (Array.isArray(value)) return !!value.length;
    if (operator === NullOperator.IS_NULL || operator === NullOperator.IS_NOT_NULL) return !value;
    return !!value || value === 0;
};

const isValidDetailsFilter = (filter: SegmentFilterState<SegmentFilterCategorySpecificField>) => {
    const { categorySpecificFields, name, operator } = filter;
    const { value } = categorySpecificFields;

    return !!name && !!operator && isValidValue(value, operator);
};

const isValidSubFilter = (subFilter: SegmentFilterEventCondition) => {
    const { name, operator, value } = subFilter;
    return !!name && !!operator && isValidValue(value, operator);
};

export const isValidEventsFilter = (filter: SegmentFilterState<SegmentFilterEventFields>) => {
    const { categorySpecificFields, name, operator } = filter;
    const { value, conditions } = categorySpecificFields;

    const { dateRangeDateTimeOperator } =
        'dateRangeDateTimeOperator' in categorySpecificFields && categorySpecificFields;

    if (!value && !conditions?.length) {
        return !!operator && !!name;
    }

    if (dateRangeDateTimeOperator) {
        return !!value;
    }

    if (!!conditions?.length) {
        const invalidSubFilter = conditions.find((condition) => !isValidSubFilter(condition));
        return !invalidSubFilter;
    }
};

const isFilterValid = (filter: SegmentFilterState<SegmentFilterCategorySpecificField>) => {
    const { category } = filter;

    if (!category) return false;

    if (category === FilterCategory.CONTACT_DETAILS) {
        return isValidDetailsFilter(filter);
    }

    if (category === FilterCategory.CONTACT_ACTIVITY) {
        return isValidActivityFilter(filter as SegmentFilterState<SegmentFilterEngagementFields>);
    }

    if (category === FilterCategory.CONTACT_EVENTS) {
        return isValidEventsFilter(filter);
    }
};

export const isSegmentFiltersValid = (
    filters: SegmentFilterState<SegmentFilterCategorySpecificField>[],
    isRecordTypesEmpty: boolean
): boolean => {
    if (filters.length === 0) return !isRecordTypesEmpty;

    const hasInvalidFilters = filters.some((filter) => !isFilterValid(filter));

    return !hasInvalidFilters;
};

/**
 * Validates the nested filter tree by flattening all leaf filters
 * and checking each one with the existing per-filter validation.
 */
export const isNestedSegmentFiltersValid = (rootGroup: FilterGroupNode, isRecordTypesEmpty: boolean): boolean => {
    const allFilters = flattenFilters(rootGroup);
    return isSegmentFiltersValid(allFilters, isRecordTypesEmpty);
};

// ─── Nested segment definition conversion ────────────────────────────────

const categoryMap: Record<string, string> = {
    [FilterCategory.CONTACT_ACTIVITY]: 'engagement',
    [FilterCategory.CONTACT_DETAILS]: 'demographic',
    [FilterCategory.CONTACT_EVENTS]: 'contactEvent',
};

/**
 * Converts a single FilterGroupNode into the API's segment definition shape.
 * Leaf filters are grouped by category; child groups become subsegments.
 */
const convertGroupToDefinition = (
    group: FilterGroupNode,
    options?: { hasAllContacts: boolean; recordTypes: RecordTypeFilter['recordType'][] }
): NestedSegmentDefinitionInput => {
    const leafFilters: SegmentFilterState<SegmentFilterCategorySpecificField>[] = [];
    const subsegments: NestedSegmentDefinitionInput[] = [];

    for (const child of group.children) {
        if (child.type === 'filter') {
            leafFilters.push(child.filter);
        } else if (child.type === 'group' && child.group) {
            subsegments.push(convertGroupToDefinition(child.group));
        }
    }

    const { contactEvent = [], demographic = [], engagement = [] } = groupBy(
        leafFilters,
        (filter) => categoryMap[filter.category] || ''
    );

    return {
        matchType: group.matchType,
        ...(options && !options.hasAllContacts && { contactRecordType: options.recordTypes }),
        demographic: (demographic as SegmentFilterState<SegmentFilterDemographicFields>[]).map(
            convertFilterToDemographic
        ),
        engagement: (engagement as SegmentFilterState<SegmentFilterEngagementFields>[]).map(convertFilterToEngagement),
        contactEvent: (contactEvent as SegmentFilterState<SegmentFilterEventFields>[]).map(convertFilterToEvent),
        ...(subsegments.length > 0 && { subsegments }),
    };
};

/**
 * Converts the full UI tree (rootGroup) into the nested API schema.
 * Only the root level includes contactRecordType.
 */
export const getNestedSegmentDefinition = ({
    rootGroup,
    hasAllContacts,
    recordTypes,
}: {
    rootGroup: FilterGroupNode;
    hasAllContacts: boolean;
    recordTypes: RecordTypeFilter['recordType'][];
}): NestedSegmentDefinitionInput => convertGroupToDefinition(rootGroup, { hasAllContacts, recordTypes });

/**
 * Recursively finds the first engagement filter name across the entire
 * SegmentDefinition tree (including subsegments). Returns undefined if none found.
 */
export const findFirstEngagementName = (definition: SegmentDefinition): string | undefined => {
    if (definition.engagement?.length) {
        return definition.engagement[0].name;
    }

    for (const sub of definition.subsegments || []) {
        const found = findFirstEngagementName(sub);
        if (found) return found;
    }

    return undefined;
};

// ─── API definition → UI tree conversion (copy audience flow) ────────

/**
 * Recursively converts an API SegmentDefinition (with optional subsegments)
 * into a FilterGroupNode tree suitable for the form state.
 */
export const buildTreeFromDefinition = (
    definition: SegmentDefinition,
    sends: Send[],
    getNextId: () => number
): FilterGroupNode => {
    const leafChildren: FilterTreeNode[] = [
        ...(definition.contactEvent?.map((event) => createFilterLeafNode(getEventFilter(event, getNextId()))) || []),
        ...(definition.demographic?.map((demo) => createFilterLeafNode(getDemographicFilter(demo, getNextId()))) || []),
        ...(sends?.length
            ? definition.engagement?.map((eng) => createFilterLeafNode(getEngagementFilter(eng, getNextId(), sends))) ||
              []
            : []),
    ];

    const groupChildren: FilterTreeNode[] = (definition.subsegments || []).map((subsegment) => ({
        type: 'group' as const,
        id: generateNodeId(),
        group: buildTreeFromDefinition(subsegment, sends, getNextId),
    }));

    return {
        id: generateNodeId(),
        matchType: definition.matchType || 'AND',
        children: [...leafChildren, ...groupChildren],
    };
};
